import React, { useState, useImperativeHandle, forwardRef, useMemo, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Slider,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Chip,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useGetUsers } from "../../../shared/hooks";
import { Comments, FieldControl } from "../../../shared/components";
import { useDisplayField } from "../../../hooks/useDisplayField";

export interface ActivityFormHandle {
  submit: () => void;
}

interface ActivityFormProps {
  onSave: (data: ActivityFormData) => void;
  onCancel: () => void;
  initialData?: Partial<ActivityFormData>;
  phaseOptions?: Array<{ id: number; name: string }>;
  stageGateId: number | null | undefined;
  activityId: number | undefined;
  isAllowedEdit?: boolean;
  isActionOwner?: boolean;
  isEditing: boolean;
  assetData: any[];
}

export interface ActivityFormData {
  title: string;
  phase: string;
  category: string;
  category_key: string;
  category_display_text?: string | undefined;
  phaseId?: number;
  startDate: Dayjs;
  plannedCompletionDate: Dayjs;
  actualCompletionDate?: Dayjs;
  owner: string;
  userId?: number;
  progress: number;
  is_recurring: boolean;
  frequency: string;
  recurring_day: string[];
  description: string;
  asset_id: number | null;
  monthly_pattern: 'day_of_month' | 'day_of_week';
}

export const ActivityForm = forwardRef<ActivityFormHandle, ActivityFormProps>(

  function ActivityForm({ onSave, onCancel, initialData, phaseOptions = [], stageGateId, activityId, isAllowedEdit, isActionOwner, isEditing, assetData }, ref) {

    const weekdays = [
      { label: 'M', value: 'monday', key: '0', },
      { label: 'T', value: 'tuesday', key: '1', },
      { label: 'W', value: 'wednesday', key: '2', },
      { label: 'T', value: 'thursday', key: '3', },
      { label: 'F', value: 'friday', key: '4', },
      { label: 'S', value: 'saturday', key: '5', },
      { label: 'S', value: 'sunday', key: '6', },
    ];

    const { getFilterOptions } = useDisplayField([]);
    const [currentTab, setCurrentTab] = useState(0);
    const { data: usersData, isLoading: isLoadingUsers } = useGetUsers();

    const userOptions = useMemo(() => {
      if (!usersData?.data) return [];
      return usersData.data
        .filter(user => user.is_active === "1")
        .map(user => ({
          id: user.user_id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
        }));
    }, [usersData]);

    const [formData, setFormData] = useState<ActivityFormData>({
      title: initialData?.title || "",
      phase: initialData?.phase || "",
      category: initialData?.category || "",
      category_key: initialData?.category_key || "",
      phaseId: initialData?.phaseId,
      startDate: initialData?.startDate || dayjs(),
      plannedCompletionDate: initialData?.plannedCompletionDate || dayjs(),
      actualCompletionDate: initialData?.actualCompletionDate,
      owner: initialData?.owner || "",
      userId: initialData?.userId,
      progress: initialData?.progress || 0,
      is_recurring: initialData?.is_recurring || false,
      frequency: initialData?.frequency || "",
      recurring_day: initialData?.recurring_day && (initialData?.recurring_day?.map((r) => r && r.toString())) || ['4'],
      description: initialData?.description || "",
      asset_id: initialData?.asset_id ?? null,
      monthly_pattern: initialData?.monthly_pattern || 'day_of_week',
    });

    const getMonthlyDescription = () => {
      if (!formData.startDate) return '';
      const date = dayjs(formData.startDate);
      const dayOfMonth = date.date();
      const dayName = date.format('dddd');
      const weekOfMonth = Math.ceil(dayOfMonth / 7);
      const ordinal = ['first', 'second', 'third', 'fourth', 'fifth'][weekOfMonth - 1] || weekOfMonth;
      return formData.monthly_pattern === 'day_of_month'
        ? `On day ${dayOfMonth} of every month`
        : `On the ${ordinal} ${dayName} of every month`;
    };

    const toggleWeekday = (dayKey: string) => {
      const currentWeekdays = formData.recurring_day || [];
      const newWeekdays = currentWeekdays.includes(dayKey)
        ? currentWeekdays.filter((d: string) => d !== dayKey)
        : [dayKey?.toString()];
      setFormData({ ...formData, recurring_day: newWeekdays });
    };

    useEffect(() => {
      if (initialData) {
        setFormData((prev) => ({
          ...prev,
          title: initialData.title ?? prev.title,
          phase: initialData.phase ?? prev.phase,
          phaseId: initialData.phaseId ?? prev.phaseId,
          category:
            initialData?.category ?? prev.category,
          category_key: initialData?.category_key ?? prev.category_key,
          startDate: initialData.startDate ?? prev.startDate,
          plannedCompletionDate: initialData.plannedCompletionDate ?? prev.plannedCompletionDate,
          actualCompletionDate: initialData.actualCompletionDate ?? prev.actualCompletionDate,
          owner: initialData.owner ?? prev.owner,
          userId: initialData.userId ?? prev.userId,
          progress: initialData.progress ?? prev.progress,
          asset_id: initialData.asset_id ?? prev.asset_id,
          is_recurring: initialData.is_recurring ?? prev.is_recurring,
          frequency: initialData.frequency ?? prev.frequency,
          recurring_day: initialData.recurring_day && (initialData.recurring_day.map((r) => r && r.toString())) || prev.recurring_day,
          monthly_pattern: initialData.monthly_pattern ?? prev.monthly_pattern,
        }));
      }
    }, [initialData]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        onSave(formData);
      },
    }));

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tab label="OVERVIEW" />
            <Tab label="COMMENTS" disabled={!stageGateId || !activityId} />
          </Tabs>

          {currentTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, px: 2, pb: 2 }}>

              <TextField
                fullWidth
                label="Activity Title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                disabled={isEditing ? !isAllowedEdit : false}
              />

              <FormControl fullWidth>
                <InputLabel>Phase</InputLabel>
                <Select
                  value={formData.phaseId || ""}
                  label="Phase"
                  onChange={(e) => {
                    const selectedPhase = phaseOptions.find(
                      (p) => p.id === e.target.value
                    );
                    setFormData({
                      ...formData,
                      phaseId: e.target.value as number,
                      phase: selectedPhase?.name || "",
                    });
                  }}
                  disabled={isEditing ? !isAllowedEdit : false}
                >
                  {phaseOptions.length > 0 ? (
                    phaseOptions.map((phase) => (
                      <MenuItem key={phase.id} value={phase.id}>
                        {phase.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No phases available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              <FieldControl
                keyName={"category_id"}
                label={"Category"}
                variant={"select"}
                value={formData.category_key || ""}
                options={getFilterOptions("ACTIVITY_CATEGORY").map((value) => {
                  return {
                    label: value.display_text,
                    value: value.key,
                  };
                })}
                onChange={(_key: string, value: any) => {
                  setFormData({ ...formData, category_key: value });
                }}
              />

              <FieldControl
                keyName={"asset_id"}
                label={"Assets"}
                variant={"select"}
                value={formData.asset_id}
                options={assetData.map((value) => {
                  return {
                    label: value.asset_description,
                    value: value.asset_id,
                  };
                })}
                onChange={(_key: string, value: any) => {
                  setFormData({ ...formData, asset_id: value });
                }}
              />

              <DateTimePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(date) =>
                  date && setFormData({ ...formData, startDate: date })
                }
                slotProps={{ textField: { fullWidth: true } }}
                disabled={isEditing ? !isAllowedEdit : false}
                format="DD/MM/YYYY HH:mm"
              />

              <DateTimePicker
                label="Planned Completion Date"
                value={formData.plannedCompletionDate}
                onChange={(date) =>
                  date && setFormData({ ...formData, plannedCompletionDate: date })
                }
                slotProps={{ textField: { fullWidth: true } }}
                disabled={isEditing ? !isAllowedEdit : false}
                format="DD/MM/YYYY HH:mm"
              />

              <DateTimePicker
                label="Actual Completion Date"
                value={formData.actualCompletionDate || null}
                onChange={(date) =>
                  setFormData({
                    ...formData,
                    actualCompletionDate: date || undefined,
                  })
                }
                slotProps={{ textField: { fullWidth: true } }}
                disabled={isEditing ? !isActionOwner : false}
                minDate={formData.startDate}
                format="DD/MM/YYYY HH:mm"
              />

              <FormControl fullWidth>
                <InputLabel>Activity Owner</InputLabel>
                <Select
                  value={formData.userId || ""}
                  label="Activity Owner"
                  onChange={(e) => {
                    const selectedUser = userOptions.find(u => u.id === e.target.value);
                    setFormData({
                      ...formData,
                      userId: e.target.value as number,
                      owner: selectedUser?.name || ""
                    });
                  }}
                  disabled={isLoadingUsers || (isEditing ? !isAllowedEdit : false)}
                >
                  {isLoadingUsers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : userOptions.length > 0 ? (
                    userOptions.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No users available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              <Box sx={{ px: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Percentage Completed
                </Typography>
                <Slider
                  value={formData.progress}
                  onChange={(_, value) =>
                    setFormData({ ...formData, progress: value as number })
                  }
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={0}
                  max={100}
                  sx={{ mt: 2 }}
                  disabled={isEditing ? !isActionOwner : false}
                />
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
                >
                  <Typography variant="caption">0</Typography>
                  <Typography variant="caption">25</Typography>
                  <Typography variant="caption">50</Typography>
                  <Typography variant="caption">70</Typography>
                  <Typography variant="caption">100%</Typography>
                </Box>
              </Box>

              <FormControlLabel
                sx={{ px: 1 }}
                key={formData.is_recurring ? "recurring-true" : "recurring-false"}
                control={<Checkbox checked={formData.is_recurring} onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })} />}
                label={"Is Recurring"}
                disabled={isEditing ? !isAllowedEdit : false}
              />

              {formData.is_recurring && (
                <FieldControl
                  keyName={""}
                  label={"Frequency"}
                  variant={"select"}
                  value={formData.frequency}
                  options={getFilterOptions("FREQUENCY").map(opt => ({ value: opt.key, label: opt.display_text }))}
                  onChange={(_key: string, value: any) => { setFormData({ ...formData, frequency: value }) }}
                  disabled={isEditing ? !isAllowedEdit : false}
                />
              )}

              {formData.frequency === 'weekly' && (
                <Box display={'flex'} flexDirection={'column'} gap={1}>
                  <FormHelperText sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                    Select days of the week
                  </FormHelperText>
                  <Box display={'flex'} alignItems={'center'} gap={1}>
                    {weekdays.map((day) => (
                      <Chip
                        key={day.value}
                        label={day.label}
                        onClick={() => toggleWeekday(day.key)}
                        color={(formData.recurring_day || []).includes(day.key.toString()) ? 'primary' : 'default'}
                        variant={(formData.recurring_day || []).includes(day.key.toString()) ? 'filled' : 'outlined'}
                        clickable
                        disabled={isEditing ? !isAllowedEdit : false}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    happens on every
                    {
                      (formData.recurring_day || []).length > 0
                        ? ` ${(formData.recurring_day || []).join(', ')}`
                        : ' ...no days selected'
                    }
                  </Typography>
                </Box>
              )}

              {formData.frequency === 'monthly' && (
                <Box display={'flex'} flexDirection={'column'} gap={1}>
                  <FieldControl
                    keyName={"monthly_pattern"}
                    label={"Monthly Pattern"}
                    variant={"radio"}
                    onChange={(_key: string, value: any) => {
                      setFormData({ ...formData, monthly_pattern: value });
                    }}
                    disabled={isEditing ? !isAllowedEdit : false}
                    value={formData.monthly_pattern}
                    options={getFilterOptions('MONTHLY_RECURRENCE_PATTERN').map((opt: any) => ({
                      value: opt.value,
                      label: opt.display_text
                    }))}
                  />
                  {formData.startDate && (
                    <FormHelperText sx={{ color: 'text.secondary', ml: 1 }}>
                      {getMonthlyDescription()}
                    </FormHelperText>
                  )}
                </Box>
              )}

              <FormControl fullWidth>
                <TextField
                  label="Description"
                  multiline
                  minRows={4}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  disabled={isEditing ? !isAllowedEdit : false}
                />
              </FormControl>

            </Box>
          )}

          {currentTab === 1 && stageGateId && activityId && (
            <Box sx={{ textAlign: "center" }} flex={1} px={2} pb={2}>
              <Comments topic={['SCHEDULING_AND_PLANNING']} sub_event={['ACTIVITY_COMMENT_ADDED']} object_id={String(activityId)} parent_id={String(stageGateId)} isDisabled={isAllowedEdit || isActionOwner} />
            </Box>
          )}

        </Box>
      </LocalizationProvider>
    );
  }
);

export const ActivityFormActions: React.FC<{
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
  isDeleteAllowed?: boolean;
}> = ({ onSave, onCancel, onDelete, isEditing, isDeleteAllowed }) => (
  <>
    <Button
      variant="outlined"
      onClick={onCancel}
      sx={{ textTransform: "uppercase" }}
    >
      Cancel
    </Button>
    {isEditing && onDelete && isDeleteAllowed && (
      <Button
        variant="outlined"
        color="error"
        onClick={onDelete}
        sx={{ textTransform: "uppercase" }}
      >
        Delete
      </Button>
    )}
    <Button
      variant="contained"
      onClick={onSave}
      sx={{ textTransform: "uppercase", bgcolor: "#E84B0F" }}
    >
      Save Details
    </Button>
  </>
);
